'use strict';

/**
 * Customer progress from a mapped PortalApi snapshot.
 * Publications choose the current correlation. Graph nodes decide Ready and Failed.
 */

import { buildPipelineProgressModel } from './pipelineProgressModel';
import {
	buildCorePipelineProgressModel,
	deriveSampleSetFlowFromGraph,
} from './pipelineCoreProgressModel';
import {
	CORE_PROGRESS_COPY,
	CORE_PROGRESS_STAGE_LABELS,
	CORE_PROGRESS_STAGE_ORDER,
	DISCOVERY_PROGRESS_COPY,
	KITS_PROGRESS_COPY,
	KITS_PROGRESS_STAGE_LABELS,
	KITS_PROGRESS_STAGE_ORDER,
	MERGED_PROGRESS_COPY,
	MERGED_PROGRESS_STAGE_LABELS,
	MERGED_PROGRESS_STAGE_MESSAGES,
	MERGED_PROGRESS_STAGE_ORDER,
	PIPELINE_PROGRESS_STATE_MESSAGES,
} from './pipelineProgressCopy';
import { buildPipelineStatusGraphViewModel } from './pipelineStatusAdapter';
import { selectCurrentV7Run } from './usePipelineStatus';
import { requirePipelineStatusFlowScenarioKey } from './pipelineStatusFlowScenarios';

/**
 * @param {'core' | 'discoveryPanels' | 'kits'} pipelineKind
 */
function publicationPipeline(pipelineKind) {
	if (pipelineKind === 'discoveryPanels') return 'discoverypanels';
	return pipelineKind;
}

/**
 * @param {{
 *   kind: string,
 *   facts: import('./pipelineStatusTypes').PipelineStatusFact[],
 *   definition: import('./pipelineStatusTypes').PipelineStatusGraphDefinition | null,
 *   currentFlows: ReturnType<import('./usePipelineStatus').mapApiCurrentFlows>,
 *   projectId?: string,
 * }} input
 * @param {'core' | 'discoveryPanels' | 'kits'} pipelineKind
 * @param {{
 *   copy?: typeof CORE_PROGRESS_COPY,
 *   stageOrder?: readonly string[],
 *   stageLabels?: Record<string, string>,
 * }} options
 */
function sampleSetModel(input, pipelineKind, options) {
	if (input.kind !== pipelineKind || input.currentFlows == null || input.definition == null) {
		return buildCorePipelineProgressModel([], { ...options, supported: false });
	}
	const want = publicationPipeline(pipelineKind);
	const publications = input.currentFlows.publications.filter(
		(p) => String(p.pipeline ?? '').trim().toLowerCase() === want,
	);
	const flows = publications.map((publication) => deriveSampleSetFlowFromGraph({
		publication,
		facts: input.facts,
		definition: input.definition,
		projectId: input.projectId,
		stageOrder: options.stageOrder ?? CORE_PROGRESS_STAGE_ORDER,
		stageLabels: options.stageLabels ?? CORE_PROGRESS_STAGE_LABELS,
	}));
	return buildCorePipelineProgressModel(flows, options);
}

/**
 * @param {{
 *   kind: 'v7' | 'core' | 'discoveryPanels' | 'kits' | 'merged' | 'unsupported',
 *   flowScenarioKey: string,
 *   facts: import('./pipelineStatusTypes').PipelineStatusFact[],
 *   iterationManifests: import('./pipelineStatusTypes').PipelineStatusIterationManifest[],
 *   definition: import('./pipelineStatusTypes').PipelineStatusGraphDefinition | null,
 *   currentFlows: ReturnType<import('./usePipelineStatus').mapApiCurrentFlows>,
 *   projectId?: string,
 * }} input
 */
export function buildPipelineProgressBundleFromSnapshot(input) {
	const kind = input.kind;
	requirePipelineStatusFlowScenarioKey(input.flowScenarioKey);
	const projectId = input.projectId != null ? String(input.projectId) : '';

	const publicationPipelineName = kind === 'v7' ? 'v7' : kind === 'merged' ? 'merged' : null;
	const scoped = publicationPipelineName
		? selectCurrentV7Run(input.facts, input.iterationManifests, input.currentFlows, publicationPipelineName)
		: {
			facts: input.facts,
			iterationManifests: input.iterationManifests,
			currentV7CorrelationId: null,
		};

	const definition = input.definition;
	const stages = definition
		? buildPipelineStatusGraphViewModel(
			definition,
			scoped.facts,
			scoped.iterationManifests,
			projectId,
		)
		: [];

	const projectPublication = publicationPipelineName
		? input.currentFlows?.publications?.find(
			(p) => String(p.pipeline ?? '').trim().toLowerCase() === publicationPipelineName,
		)
		: null;
	const mergedProgressOptions = kind === 'merged'
		? {
			stageOrder: MERGED_PROGRESS_STAGE_ORDER,
			stageLabels: MERGED_PROGRESS_STAGE_LABELS,
			stageMessages: MERGED_PROGRESS_STAGE_MESSAGES,
			stateMessages: {
				...PIPELINE_PROGRESS_STATE_MESSAGES,
				ready: MERGED_PROGRESS_COPY.ready,
				failed: MERGED_PROGRESS_COPY.failed,
				unavailable: MERGED_PROGRESS_COPY.unavailable,
			},
		}
		: {};
	const projectKind = kind === 'v7' || kind === 'merged';
	const model = projectKind && input.currentFlows == null
		? buildPipelineProgressModel([], { supported: false, ...mergedProgressOptions })
		: buildPipelineProgressModel(stages, {
			supported: projectKind,
			startedAt: projectPublication?.startedAt,
			...mergedProgressOptions,
		});
	const coreModel = sampleSetModel(input, 'core', { copy: CORE_PROGRESS_COPY });
	const discoveryModel = sampleSetModel(input, 'discoveryPanels', { copy: DISCOVERY_PROGRESS_COPY });
	const kitsModel = sampleSetModel(input, 'kits', {
		copy: KITS_PROGRESS_COPY,
		stageOrder: KITS_PROGRESS_STAGE_ORDER,
		stageLabels: KITS_PROGRESS_STAGE_LABELS,
	});

	return {
		kind,
		model,
		coreModel,
		discoveryModel,
		kitsModel,
		stages,
		definition,
		currentFlows: input.currentFlows,
		facts: scoped.facts,
		iterationManifests: scoped.iterationManifests,
		currentV7CorrelationId: scoped.currentV7CorrelationId,
	};
}
